import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { StyleSheet, View } from "react-native";
import type { CompositeScreenProps } from "@react-navigation/native";
import type { BottomTabScreenProps } from "@react-navigation/bottom-tabs";
import type { StackScreenProps } from "@react-navigation/stack";
import { useTheme } from "../../hooks/use-theme";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useQuery } from "@apollo/client";
import Button from "../../components/Button";
import { FIND_MY_MISSIONS } from "../../graphql/home";
import Loading from "../../components/Loading";
import { useNavigation } from "@react-navigation/native";
import { WorkerTabParamList } from "../../navigations/worker/Home";
import NoActivitiesImage from "../../assets/images/no-activities-image.svg";
import { useI18n } from "../../hooks/i18n-hooks";
import { AuthManagerContext } from "../../contexts/AuthManager";
import ScreenCard from "../ScreenCard";
import { ProfileType, Mission } from "../../__generated__/graphql";
import ListGroup from "../../components/ListGroup";
import { useMissionDisplayAttributes } from "../../hooks/missions";
import EmptyScreen from "../../components/EmptyScreen";
import { useTracking } from "../../hooks/tracking";
import { useDynamicHeaderButton } from "../../hooks/use-dynamic-header-button";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { useIsScreenSizeMin } from "../../hooks/useIsScreenSizeMin";
import { SHORT_DATE_IN_LIST_FORMAT_OPTIONS, useDateFormatter } from "../../hooks/date";
import { buildErrorHeadband } from "../ScreenCardHeadband";
import { LinkSmallTextBold } from "../../components/FormattedText";
import { useIntercom } from "../../hooks/intercom";
import Gap from "../../ui_atoms/Gap";

type EmptyMissionsProps = {
    onCreateMission: () => void;
    onInviteClient: () => void;
    hasTalent: boolean;
    hasClient: boolean;
};

const EmptyMissions: React.FC<EmptyMissionsProps> = ({
    onCreateMission,
    onInviteClient,
    hasTalent,
    hasClient,
}) => {
    const styles = useStyles();
    const i18n = useI18n();
    const { profileType } = useContext(ProfileSwitcherManagerContext);
    const isLarge = useIsScreenSizeMin("large");

    const { trackEvent } = useTracking();

    useEffect(() => {
        if (profileType) {
            void trackEvent("show_empty_mission_list", { profileType }, true);
        }
    }, [trackEvent, profileType]);

    return (
        <>
            <EmptyScreen
                image={NoActivitiesImage}
                text={i18n.t(
                    profileType === ProfileType.Client
                        ? hasTalent
                            ? "screens.user.missions.create_mission_now"
                            : `screens.user.missions.no_talents${isLarge ? "_left" : ""}`
                        : hasClient
                          ? "screens.user.missions.has_invites"
                          : "screens.user.missions.no_invites",
                )}
            />
            {profileType === ProfileType.Client && hasTalent && (
                <Button
                    size="large"
                    testID="button-new-mission"
                    style={styles.startButton}
                    onPress={onCreateMission}
                    label={i18n.t("screens.user.missions.create_mission")}
                />
            )}
            {profileType === ProfileType.Worker && !hasClient && (
                <Button
                    size="large"
                    testID="button-invite-client"
                    buttonMode="secondary"
                    style={styles.startButton}
                    onPress={onInviteClient}
                    label={i18n.t("screens.user.missions.invite_client")}
                />
            )}
        </>
    );
};

type MissionsScreenProps = CompositeScreenProps<
    BottomTabScreenProps<WorkerTabParamList, "MissionsScreen">,
    StackScreenProps<RootStackParamList>
>;

export default function MissionsScreen() {
    const styles = useStyles();
    const i18n = useI18n();
    const { profileType } = useContext(ProfileSwitcherManagerContext);
    const { user, refreshUser } = useContext(AuthManagerContext);
    const { presentArticleId } = useIntercom();
    const isLarge = useIsScreenSizeMin("large");

    const {
        loading: missionsLoading,
        error: missionsError,
        data: missionsData,
        refetch: missionsRefetch,
    } = useQuery(FIND_MY_MISSIONS, {
        skip: !profileType,
    });

    // https://reactnavigation.org/docs/typescript/#annotating-usenavigation. But typescript
    // annotation is not working well so I had to add ["navigation"] to make it work
    const navigation = useNavigation<MissionsScreenProps["navigation"]>();

    const refresh = useCallback(async () => {
        // refresh user is required to update the clientProfile.clientRelationshipCount
        return await Promise.all([refreshUser?.(), missionsRefetch()]);
    }, [missionsRefetch, refreshUser]);

    const { trackEvent } = useTracking();

    const clientProfile = useCurrentProfile();

    const allMissions = useMemo(
        () => missionsData?.findMyMissions?.elements || undefined,
        [missionsData?.findMyMissions?.elements],
    );

    const hasTalent = useMemo(
        () => (clientProfile?.clientRelationshipCount || 0) > 0,
        [clientProfile?.clientRelationshipCount],
    );

    const hasClient = useMemo(
        () => (user?.workerProfile?.relationshipCount || 0) > 0,
        [user?.workerProfile?.relationshipCount],
    );

    const isClient = profileType === ProfileType.Client;
    const intDateFormatter = useDateFormatter();

    const formatDate = useCallback(
        (d?: string | null, empty = "") => {
            if (!d) {
                return empty;
            }
            return intDateFormatter(new Date(d), SHORT_DATE_IN_LIST_FORMAT_OPTIONS);
        },
        [intDateFormatter],
    );

    const onCreateMission = React.useCallback(() => {
        void trackEvent("click_create_mission", {}, true);
        if (isClient) {
            navigation.navigate("CreateMissionStack");
        }
    }, [isClient, navigation, trackEvent]);

    const onInviteClient = React.useCallback(() => {
        navigation.navigate("InviteClientInfo");
    }, [navigation]);

    const onSelectMission = React.useCallback(
        (missionId: string) => {
            navigation.navigate("MissionDetails", { missionId });
        },
        [navigation],
    );

    const getMissionDisplayAttributes = useMissionDisplayAttributes();

    const items = useMemo(() => {
        if (!allMissions) {
            return [];
        }

        return [...allMissions].map((mission: Mission) => {
            const title =
                profileType === ProfileType.Client
                    ? mission.workerInfo.name
                    : mission.clientInfo.name;
            const avatarPath =
                profileType === ProfileType.Client
                    ? mission.workerInfo.avatarUrl
                    : mission.clientInfo.logoUrl;
            const avatarEmail =
                profileType === ProfileType.Client
                    ? mission.workerInfo.email
                    : mission.clientInfo.email;

            const jobTitle = mission.currentContract?.jobTitle;
            const missionName = mission.name;
            const displayAttributes = getMissionDisplayAttributes(mission, profileType);
            const description = `${missionName || jobTitle || ""}\n\n${displayAttributes.durationInfo}`;
            const isCurrentUserOwner = mission.clientInfo.id === clientProfile?.id;
            return {
                value: mission.id,
                title,
                avatarPath,
                avatarEmail,
                description,
                labels: [
                    {
                        text: displayAttributes.missionStatus,
                        mode: displayAttributes.labelMode || ("default" as const),
                    },
                    ...(isCurrentUserOwner
                        ? [
                              {
                                  text: i18n.t("mission.details.owner"),
                                  mode: "secondary" as const,
                              },
                          ]
                        : []),
                ],
                tags: [
                    formatDate(mission.createdAt),
                    ...(profileType === ProfileType.Worker ? [mission.clientInfo.profileName] : []),
                ],
                useBigAvatarOrIcon: true,
                onPress: () => {
                    onSelectMission(mission.id);
                },
            };
        });
    }, [
        allMissions,
        profileType,
        getMissionDisplayAttributes,
        clientProfile?.id,
        i18n,
        formatDate,
        onSelectMission,
    ]);

    const dynamicButtonProps = useDynamicHeaderButton(
        profileType === ProfileType.Client && hasTalent && !missionsError && items.length > 0
            ? {
                  title: i18n.t("screens.user.missions.create_new_mission"),
                  shortTitle: i18n.t("mission.nav_title"),
                  onClick: onCreateMission,
              }
            : profileType === ProfileType.Worker && !missionsError && items.length > 0 && !isLarge
              ? {
                    title: i18n.t("screens.user.missions.invite_client"),
                    shortTitle: i18n.t("screens.user.missions.invite_client_short"),
                    onClick: onInviteClient,
                    icon: "arrow-right-from-bracket",
                }
              : undefined,
    );

    const isInitialLoading = !missionsData && missionsLoading;

    return (
        <View style={styles.container}>
            <View style={{ flex: 1 }}>
                <ScreenCard
                    refresh={refresh}
                    isContentContainerFlex={!missionsError && (!items.length || isInitialLoading)}
                    headband={
                        missionsError
                            ? buildErrorHeadband(
                                  i18n.t("screens.user.missions.cannot_load_missions"),
                              )
                            : undefined
                    }
                    {...dynamicButtonProps}
                >
                    {isInitialLoading ? (
                        <Loading />
                    ) : (
                        <>
                            {missionsError ? undefined : items.length === 0 ? (
                                <EmptyMissions
                                    onCreateMission={onCreateMission}
                                    onInviteClient={onInviteClient}
                                    hasTalent={hasTalent}
                                    hasClient={hasClient}
                                />
                            ) : (
                                <ListGroup
                                    itemStyle={styles.listGroupItem}
                                    isItemsCheckable={false}
                                    items={items}
                                    alignment="stretch"
                                />
                            )}
                            <Gap gap={1} />
                            <LinkSmallTextBold
                                style={{ alignSelf: "center" }}
                                onPress={() => {
                                    if (profileType === ProfileType.Client) {
                                        void presentArticleId("9983409");
                                    } else if (profileType === ProfileType.Worker) {
                                        void presentArticleId("9983402");
                                    }
                                }}
                            >
                                {i18n.t("screens.user.missions.know_more_about_missions")}
                            </LinkSmallTextBold>
                        </>
                    )}
                </ScreenCard>
            </View>
        </View>
    );
}

const useStyles = () => {
    const { gridSize } = useTheme();
    const isLarge = useIsScreenSizeMin("large");

    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    width: "100%",
                    height: "100%",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                },
                startButton: {
                    marginHorizontal: gridSize,
                    alignSelf: !isLarge ? "stretch" : "center",
                },
                listGroupItem: {
                    paddingBottom: gridSize,
                },
                fab: { position: "absolute", margin: gridSize / 2, right: 0, bottom: 0 },
            }),
        [gridSize, isLarge],
    );
};
